/**
 * The content license agreement should not be translated via i18ns or Crowdin.
 */

import { Heading, Text } from '@youversion/design-system-components';
import React from 'react';

import { useAppLanguage } from '@/context/appLanguage';
import dayjs from '@/lib/dayjs';
import { localizeDate } from '@/utils/localize-date';

export const LICENSE_VERSION = '10/09/2023';

// add props

function H1({ children }: { children: string }) {
  return (
    <Heading level={1} size='xl' variant='headline'>
      {children}
    </Heading>
  );
}

function H2({ children }: { children: string }) {
  return (
    <Heading className='mbs-3' level={2} size='lg' variant='headline'>
      {children}
    </Heading>
  );
}

function H3({ children }: { children: string }) {
  return (
    <Heading className='mbs-3' level={3} size='md' variant='headline'>
      {children}
    </Heading>
  );
}

function P({ children }: { children: string | Array<string> }) {
  return (
    <Text className='mbs-1 mbe-1' variant='paragraph'>
      {children}
    </Text>
  );
}

function LI({ children }: { children: string }) {
  return (
    <Text className='mbe-1' component='li' variant='paragraph'>
      {children}
    </Text>
  );
}

export function YouVersionContentContributionAgreement() {
  const { appLanguage } = useAppLanguage();

  return (
    <div className='text-13 md:text-16'>
      <H1>YouVersion Content Contribution Agreement</H1>

      <P>Revision: {localizeDate({ appLanguage, date: dayjs(LICENSE_VERSION).toDate() })}</P>

      <H2>Brief Overview</H2>

      <P>Here’s a summary of what you can expect to ﬁnd in our Content Contribution Agreement:</P>

      <H3>It’s Your Content.</H3>

      <P>We want to make sure it’s really clear that you own the content, and we own the platforms.</P>

      <H3>It’s About the Community.</H3>

      <P>
        The purpose of offering your content is to help people all over the world engage with the Bible. We take our
        users’ privacy very seriously, so this agreement makes it clear how we honor that promise. We also know you want
        to make sure the content provided is the best quality possible, so this agreement allows us to fix errors and
        issues found in the content.
      </P>

      <H3>It’s a Partnership.</H3>

      <P>
        This agreement covers the scope of our partnership, and the ease of either party canceling the agreement, if
        desired. We also want to make sure both parties are equipped to properly cross-market the content to help even
        more people engage with the Bible.
      </P>

      <H2>Content Contribution Agreement</H2>

      <P>
        Thanks for your interest in partnering with us to provide content to the YouVersion Community. Here’s some
        information that helps define the scope of our relationship: YouVersion, Inc., a non-profit charitable
        corporation with an address of 4600 East 2nd St., Edmond, Oklahoma, and its parents, affiliates, and
        subsidiaries, (“YouVersion,” “us,” or “we”), operates online and mobile platforms. The entity or individual
        entering into this agreement (“Agreement”) as “Content Provider” (“You” or “Content Provider”) would like to
        make certain Content (as defined herein) available on those platforms. You and YouVersion (the “parties” and
        each a “party”) each agree that this Agreement sets forth our respective rights and obligations with respect to
        that Content.
      </P>

      <H3>1. Deﬁnitions</H3>

      <ul className='list-none'>
        <LI>
          We are going to use certain terms throughout this Agreement. To help with your reading and reference, and to
          make sure this document isn’t extremely long, the following definitions apply throughout this Agreement:
        </LI>

        <LI>
          “Platforms” is in reference to the platforms of YouVersion and its affiliates, together with any related
          software and materials (excluding Content), as well as all YouVersion branded products, which may be hosted on
          or integrated into mobile applications, websites, voice assisted platforms, wearables, among others.
        </LI>

        <LI>
          When the word “Content” is used, it applies to all works and materials given by Content Provider to YouVersion
          for inclusion on the Platforms, regardless of form, including images, musical content, audio, videos, and
          text, as well as all concepts, names, images, likeness, voices, biographical information, music and graphics
          in such works and materials together with all modifications and derivatives thereof and all intellectual
          property or commercial rights in any part of the world in any of the foregoing, including copyright and moral
          rights, provided that the Content does not include Bible texts or Bible audio or rights in the same, which
          shall not be the subject to this Agreement, and if Content Provider wishes to provide Bible texts or Bible
          audio to YouVersion, an agreement separate is required.
        </LI>

        <LI>
          The word “Marks” means all business names, trademarks, service marks, logos and other designations of source,
          whether registered or unregistered, regardless of form, design or stylization.
        </LI>

        <LI>
          “Terms of Use” collectively and individually refers to all terms of service, terms of use, privacy policies
          and other policies that govern the use, accessing and provision of the Platforms, as amended from time to
          time.
        </LI>

        <LI>And “User” means the individual users of the Platforms, whether registered or not.</LI>
      </ul>

      <H3>2. Contribution to Platforms.</H3>

      <P>
        We are excited you would like us to distribute the Content on our Platforms. To do that, we need the Content and
        certain rights in the Content. Speciﬁcally, we need you agree to the following:
      </P>

      <ol className='list-[lower-latin] pis-3 md:pis-4' type='a'>
        <LI>You agree to provide YouVersion with the Content.</LI>

        <LI>
          You agree and hereby grant to YouVersion a worldwide, non-exclusive and royalty-free right, subject to the
          Terms of Use, to reproduce, publicly display, publicly perform, store and distribute the Content, provided
          that YouVersion does not resell or otherwise charge money for Content.
        </LI>

        <LI>
          You agree that YouVersion may exercise its rights under this Agreement across all Platforms and make
          modifications to the Content, including correcting typographical or other errors, as technically necessary to
          provide the Content on the Platforms, You also agree that we may promote and market the availability of the
          Content on the Platforms online (on the Platforms or otherwise), in print and otherwise.
        </LI>

        <LI>
          You agree that YouVersion may translate your Content into other languages used on the Platforms and may create
          an audio recording of your Content to allow users to listen to your Content within the Platforms. In exchange
          for granting these rights, we will supply you with a copy of each Content translation or recording at your
          request.
        </LI>

        <LI>
          You agree that YouVersion may allow users of the Platforms to view, download for offline use, and access and
          otherwise process the Content through the Platforms in accordance with the applicable Terms of Use.
        </LI>

        <LI>
          You agree that YouVersion may provide and sublicense its rights in your Content to contractors, service
          providers and other third parties solely to help us provide and support the Platforms (such as cloud hosting,
          maintenance, analysis, audit, fraud detection, communication, and development, among others).
        </LI>
      </ol>

      <P>
        All that said, YouVersion has an approval process for its Platforms and may choose not to use or display your
        Content. So, you agree that YouVersion reserves the sole right, in our discretion, to determine whether to make
        your Content accessible on the Platforms.
      </P>

      <H3>3. Cross-Marketing.</H3>

      <P>
        We think cross-marketing would be a great way to promote your Content and the Platforms. For that purpose, if we
        made the Content accessible on the Platforms, you grant us a worldwide, royalty-free, non-exclusive and
        non-transferable license to use your Marks solely to market and promote accessibility of the Content on the
        Platforms; and, in return, we grant you a worldwide, royalty-free, non-exclusive and non-transferable license to
        use YouVersion’s YOUVERSION Mark solely to market and promote accessibility of the Content on the Platforms. We
        will provide an approved YOUVERSION Mark to you for this specific purpose. You may obtain the same license for
        other YouVersion Marks with YouVersion’s prior written consent, however, you agree that you have no right to use
        the LIFE.CHURCH, or LC Marks, and that you shall not insinuate that your products or services are affiliated
        with or sponsored by YouVersion.
      </P>

      <H3>4. Ownership.</H3>

      <P>
        VERY IMPORTANT. We want to make sure you maintain ownership in your Content even though we are providing it
        through our Platforms. The following language makes sure that is the case: You own the Content subject to the
        rights you grant under this Agreement. YouVersion owns the Platforms. Each of us reserves all rights not
        explicitly granted in this Agreement. You and YouVersion each agree to promptly notify the other in the event
        either learns that Content may infringe third party rights.
      </P>

      <H3>5. Term of Agreement.</H3>

      <P>
        We want to keep this Agreement going until one of us says stop. In that regard, this Agreement will begin when
        you electronically or physically sign it and shall continue until terminated. We have the right to terminate
        this Agreement at any time, with or without cause, upon written notice, which includes email from
        partners@youversion.com. You may terminate this Agreement at any time, with or without cause, upon thirty days’
        notice, which includes email to partners@youversion.com. You agree that YouVersion may temporarily or
        permanently cease offering Content on Platforms that is alleged to infringe any third party rights, and that
        YouVersion also has the right to immediately terminate this Agreement if it believes that you have violated or
        will violate applicable law or Terms of Use or otherwise threaten the goodwill associated with YouVersion. Upon
        termination of this Agreement, the parties’ rights granted under Section 3 shall cease as well as YouVersion’s
        rights under Section 2, and you shall have no right to maintain Content on any Platform. Sections 4 through 10
        shall survive termination of this Agreement. Rights granted to Users under Section 2 shall also survive
        termination of this Agreement with respect to Content downloaded by Users prior to YouVersion removing it from
        its Platforms, if any.
      </P>

      <H3>6. User Information.</H3>

      <P>
        We value the security of our Users’ Information. To the extent that you request that YouVersion provide you with
        personally-identifiable information of its Users, YouVersion will only agree to do so where YouVersion has
        express consent from the User to do so, and in the event such information is provided with express User consent,
        you represent, warrant and agree that your use and processing of User information shall comply with all
        applicable law and Terms of Use.
      </P>

      <H3>7. Representations; Warranties; Disclaimer of Warranties.</H3>

      <P>
        Because you are providing the Content to us under license, we need you to make certain representations about
        your rights in the Content.Specifically, you represent and warrant that you are the sole owner of the Content,
        or that you have obtained all consents and permissions necessary to provide the rights granted under this
        Agreement, and that the exercise of those rights does not infringe any third party rights. Otherwise, neither
        party makes any other representation or warranty and each party disclaims any and all other representations and
        warranties, express or implied, including fitness for a particular purpose, infringement and merchantability.
      </P>

      <H3>8. Limitation of Liability.</H3>

      <P>
        You are providing the Content for free and we are providing it to our Users for free, so we think it’s best to
        limit liability under this Agreement. In that regard, here’s some important legal language: IN NO EVENT SHALL
        EITHER PARTY BE LIABLE TO THE OTHER OR ANY OF ITS AGENTS, OFFICERS, OWNERS OR REPRESENTATIVES UNDER ANY
        PROVISION OF THIS AGREEMENT FOR ANY CONSEQUENTIAL, INCIDENTAL, INDIRECT, EXEMPLARY, OR SPECIAL DAMAGES,
        INCLUDING BUT NOT LIMITED TO LOST PROFITS OR OTHER ECONOMIC LOSS, REGARDLESS OF WHETHER SUCH DAMAGES WERE/ARE
        FORESEEABLE AND WHETHER OR NOT ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THE FOREGOING LIMITATION SHALL APPLY
        REGARDLESS OF THE FORM OF ACTION AND WHETHER IN CONTRACT, BREACH OF WARRANTY, TORT, NEGLIGENCE, STRICT LIABILITY
        OR OTHERWISE.
      </P>

      <H3>9. Indemnity.</H3>

      <P>
        We hope that we are not sued or threatened with legal action as a result of the Content, but if we are you agree
        that you will indemnify and defend YouVersion and its affiliates and all their respective officers, owners and
        agents from and against all judgments, losses, civil penalties, liabilities, damages, costs, settlements and
        expenses, including reasonable attorneys’ fees and court costs, related to any third party claim, suit, action
        or proceeding that arises out of any actual or alleged breach by you of Sections 6 or 7. You also agree that
        Section 8 does not apply to this Section 9.
      </P>

      <H3>10. Miscellaneous.</H3>

      <P>
        Here’s the boilerplate. The terms of this Agreement set forth the entire agreement relating to the Content and
        the other subject matter of this Agreement and supersede all prior agreements relating to the same. This
        Agreement as well as the Terms of Use, may be amended by YouVersion at any time; we will provide you notice of
        an amendment to this Agreement via email and you agree any amended terms of this Agreement shall take effect
        immediately upon such notice. Amendments to any Terms of Use shall be identified in the respective Terms of Use.
        Neither party is an employee or agent of the other nor has the right to legally bind the other. “Includes” and
        “including” are not limiting. If any provision of this Agreement is unenforceable, that unenforceability shall
        not affect any other term of this Agreement. This Agreement, and all claims of any type that arise out of this
        Agreement, shall be governed by the laws of the State of Oklahoma, regardless of any choice of law provisions,
        and any lawsuit arising out of this Agreement shall be brought exclusively in Oklahoma County, Oklahoma. You
        agree that service of notices under this Agreement may be done via email and regular mail to the address used by
        us to communicate with you or by you to provide Content, which includes email to or from
        partners@youversion.com. If this agreement has been translated, it is for your convenience only. YouVersion
        makes no warranties or representations with respect to the accuracy of the translation. If any discrepancies are
        found between the English and the translated versions, the English Agreement will control.
      </P>

      <H3>Agreement</H3>

      <P>
        That’s it! We look forward to working with you in the future and welcome any questions you have about this
        Agreement or our Platforms.
      </P>
    </div>
  );
}
